import { useCallback, useEffect, useRef, useState } from "react";
import { Navigation, Timer, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, directionsUrl, errMsg } from "../lib/api";
import { useNow } from "../lib/useNow";

const remaining = (leaveAt, now) => Math.max(0, new Date(leaveAt).getTime() - now);

export const ActiveReservation = () => {
  const [spot, setSpot] = useState(null);
  const now = useNow();
  const alerted = useRef({});

  const load = useCallback(() => api.get("/me/reservation").then((r) => setSpot(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const ms = spot ? remaining(spot.leave_at, now) : 0;
  const mins = Math.max(1, Math.floor(ms / 60000));
  const soon = spot && ms <= 5 * 60000;

  useEffect(() => {
    document.documentElement.style.setProperty("--banner", spot ? "60px" : "0px");
  }, [spot]);

  useEffect(() => {
    if (!spot) return;
    const key = ms === 0 ? `${spot.id}-0` : soon ? `${spot.id}-5` : null;
    if (key && !alerted.current[key]) {
      alerted.current[key] = true;
      navigator.vibrate?.([200, 100, 200]);
    }
  }, [spot, ms, soon]);

  if (!spot) return null;

  const complete = async () => {
    try {
      await api.post(`/spots/${spot.id}/complete`);
      toast.success("Ottimo, buon parcheggio!");
      setSpot(null);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const label = ms === 0 ? "Il posto si sta liberando adesso!" : ms < 60000 ? `Si sta per liberare: ${Math.ceil(ms / 1000)}s` : soon ? `Si sta per liberare: ${mins} min` : `Si libera tra ${mins} min`;
  const ss = String(Math.floor((ms % 60000) / 1000)).padStart(2, "0");

  return (
    <div data-testid="active-reservation" className={`border-t backdrop-blur-md ${soon ? "bg-emerald-600/90 border-emerald-300/40" : "bg-amber-500/90 border-amber-200/40"} text-stone-950`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <Timer className={`h-5 w-5 shrink-0 ${soon ? "animate-pulse" : ""}`} />
        <div className="min-w-0 flex-1">
          <p data-testid="active-reservation-countdown" className="font-display font-extrabold leading-tight text-sm sm:text-base">
            {label}{ms > 0 && <span className="font-mono-x text-xs ml-2 opacity-80">{Math.floor(ms / 60000)}:{ss}</span>}
          </p>
          <p data-testid="active-reservation-address" className="text-xs truncate opacity-80">Il tuo posto prenotato · {spot.address}</p>
        </div>
        <a
          data-testid="active-reservation-directions"
          href={directionsUrl(spot.lat, spot.lng)}
          target="_blank"
          rel="noopener noreferrer"
          className="h-10 px-4 rounded-full bg-stone-950 text-amber-50 text-sm font-semibold inline-flex items-center gap-2 hover:bg-stone-800 transition-colors shrink-0"
        >
          <Navigation className="h-4 w-4" /> <span className="hidden sm:inline">Percorso</span>
        </a>
        <button data-testid="active-reservation-complete" onClick={complete} title="Ho parcheggiato" className="h-10 w-10 sm:w-auto sm:px-4 rounded-full bg-stone-950/15 hover:bg-stone-950/25 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors shrink-0">
          <CheckCircle2 className="h-4 w-4" /> <span className="hidden sm:inline">Ho parcheggiato</span>
        </button>
      </div>
    </div>
  );
};
